import React, { useMemo, useRef, useCallback, useEffect } from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Box, Typography } from "@mui/material";
import { useDashboardData } from '../../../contexts/DashboardDataContext';

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#AF19FF", "#FF4560", "#26A69A", "#D7263D", "#A2D5F2", "#077B8A"];

// Move categorization functions outside component to prevent recreation on each render
const categorizeAppleProduct = (prodId) => {
    if (!prodId) return "Unknown";
    
    const prodIdLower = prodId.toLowerCase();
    if (prodIdLower.includes("iphone")) return "iPhone";
    if (prodIdLower.includes("ipad")) return "iPad";
    if (prodIdLower.includes("airpods")) return "AirPods";
    if (prodIdLower.includes("beats")) return "Beats";
    if (prodIdLower.includes("macbook")) return "MacBook";
    if (prodIdLower.includes("imac")) return "iMac";
    if (prodIdLower.includes("watch")) return "Apple Watch";
    if (prodIdLower.includes("tv")) return "Apple TV";
    return "Other Apple Products";
};

const categorizeSamsungProduct = (prodId) => {
    if (!prodId) return "Unknown";
    
    const prodIdUpper = prodId.toUpperCase();
    if (prodIdUpper.startsWith('SM-')) {
        const series = prodIdUpper.substring(3, 4);
        return {
            'A': 'Galaxy A-series',
            'G': 'Galaxy S-series',
            'S': 'Galaxy S-series',
            'N': 'Galaxy Note',
            'F': 'Galaxy Fold',
            'Z': 'Galaxy Flip',
            'T': 'Galaxy Tab',
            'X': 'Galaxy Tab',
            'R': 'Galaxy Watch',
            'M': 'Galaxy M-series',
        }[series] || 'Other Samsung Models';
    }
    return "Unknown Samsung Model";
};

// Extract tooltip as a memoized component
const CustomTooltip = React.memo(({ active, payload, detailedData }) => {
    if (!active || !payload || !payload[0]) return null;
    
    const category = payload[0].name;
    const models = Array.from(detailedData[category] || []).sort();
    
    return (
        <Box sx={{
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            border: '1px solid #666',
            borderRadius: '4px',
            padding: '10px',
            maxHeight: '300px',
            overflowY: 'auto'
        }}>
            <Typography sx={{ fontWeight: 'bold', color: '#fff', mb: 1 }}>
                {category}: {payload[0].value} units
            </Typography>
            {models.map((model, idx) => (
                <Typography key={idx} sx={{ color: '#fff', fontSize: '0.9rem' }}>
                    • {model}
                </Typography>
            ))}
        </Box>
    );
});

// Extract chart as a completely standalone component with internal state management
// This ensures it only renders once regardless of parent re-renders
const StablePieChart = React.memo(({ data, detailedData }) => {
    // Internal state to track initialization - completely independent from parent
    const [renderState, setRenderState] = React.useState('initializing');
    const mountTimeRef = useRef(Date.now());
    const chartData = useRef(data).current; // Capture initial data and never change
    const chartDetailedData = useRef(detailedData).current; // Capture initial detailed data

    // Only initialize once on first render
    React.useEffect(() => {
        console.log('[StablePieChart] Mounted, scheduling render');
        
        // Ensure we only do this once per component instance
        if (renderState === 'initializing') {
            // Delay rendering slightly to ensure DOM is ready
            const timer = setTimeout(() => {
                console.log('[StablePieChart] Rendering chart content');
                setRenderState('ready');
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, [renderState]);
    
    // Placeholder while preparing
    if (renderState !== 'ready') {
        return (
            <Box sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Typography variant="body2" color="GrayText">Preparing chart...</Typography>
            </Box>
        );
    }
    
    // Actual chart - using captured refs to ensure data never changes
    return (
        <ResponsiveContainer width="100%" height="100%">
            <PieChart>
                <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={130}
                    innerRadius={50}
                    dataKey="value"
                    label={false}
                    isAnimationActive={false}
                >
                    {chartData.map((entry, index) => (
                        <Cell 
                            key={`cell-${entry.name}`} 
                            fill={COLORS[index % COLORS.length]} 
                        />
                    ))}
                </Pie>
                <Tooltip content={<CustomTooltip detailedData={chartDetailedData} />} />
                <Legend 
                    layout="vertical" 
                    align="right"
                    verticalAlign="middle"
                    formatter={(value) => {
                        const item = chartData.find(d => d.name === value);
                        const total = chartData.reduce((sum, d) => sum + d.value, 0);
                        const percent = ((item.value / total) * 100).toFixed(0);
                        return `${value} (${percent}%)`;
                    }}
                />
            </PieChart>
        </ResponsiveContainer>
    );
}, () => true); // Always return true - this component should never re-render

const ProductCategoryWidget = React.memo(({ brand = "Apple" }) => {
    // Use fallback to avoid undefined crash
    const context = useDashboardData();
    const productCategoryData = context.productCategoryData || { loading: false, data: [], error: null };
    const fetchOpenCases = context.fetchOpenCases;
    const { loading, data: casesData, error } = productCategoryData;
    
    // Add emergency render limiting
    const renderLimitRef = useRef({
        renderCount: 0,
        lastRenderTime: Date.now(),
        isLimited: false,
        startTime: Date.now()
    });
    
    // Very simplified rendering tracking to prevent render cascades
    renderLimitRef.current.renderCount++;
    const now = Date.now();
    const timeSinceStart = now - renderLimitRef.current.startTime;
    const renderRate = renderLimitRef.current.renderCount / (timeSinceStart / 1000);
    
    // Check if rendering too fast and set emergency flag if necessary
    const emergencyLimitActivated = renderRate > 10 && timeSinceStart > 1000 && renderLimitRef.current.renderCount > 15;
    if (emergencyLimitActivated) {
        console.warn(`[ProductCategoryWidget] Emergency render limit activated: ${renderLimitRef.current.renderCount} renders at ${renderRate.toFixed(1)}/sec`);
    }
    
    // Use refs to track mounting and data processing
    const isMountedRef = useRef(false);
    const processedDataRef = useRef(null);
    const chartReadyRef = useRef(false);
    const lastGoodDataRef = useRef({ processedData: [], detailedData: {} });
    const [fallbackActive, setFallbackActive] = React.useState(false);

    // Enhanced render tracking
    const renderCountRef = useRef(0);
    const renderTimestampsRef = useRef([]);
    const lastRenderTimeRef = useRef(0);
    
    // Track this render
    renderCountRef.current++;
    const currentTime = Date.now();
    renderTimestampsRef.current.push(currentTime);
    
    // Keep only the last 10 timestamps to avoid memory issues
    if (renderTimestampsRef.current.length > 10) {
        renderTimestampsRef.current.shift();
    }
    
    // Calculate time since last render
    const timeSinceLastRender = currentTime - lastRenderTimeRef.current;
    lastRenderTimeRef.current = currentTime;
     
    // Debug logging - log only if renders are happening too frequently
    if (renderCountRef.current > 3 && timeSinceLastRender < 1000) {
        console.log(`[ProductCategoryWidget] ${brand} - Frequent re-render #${renderCountRef.current}, ${timeSinceLastRender}ms after previous render`);
        console.log(`[ProductCategoryWidget] Render timestamps: `, 
            renderTimestampsRef.current.map(t => new Date(t).toISOString().substr(11, 12)));
        
        // Log parent props for debugging
        console.log(`[ProductCategoryWidget] Data status: loading=${loading}, hasData=${!!casesData}, error=${!!error}`);
    }
    
    // Add missing state variable that was referenced but not declared
    const [isVisible, setIsVisible] = React.useState(false);
    
    // Replace wasEverVisibleRef with a combination approach for more stability
    const wasEverVisibleRef = useRef(false);
    const [chartKey, setChartKey] = React.useState(0); // Used to force mount/unmount if needed
    
    // Define a stable component ID to help with debugging
    const componentId = useRef(`pcw-${brand}-${Math.random().toString(36).substr(2, 5)}`).current;

    // Enhanced data processing with deeper equality check
    const memoizedData = useMemo(() => {
        // If we don't have data, or are in a loading state, don't process
        if (!casesData || loading) {
            return { processedData: [], detailedData: {} };
        }
        
        // If already processed this exact data, use cached result
        if (processedDataRef.current?.originalData === casesData) {
            return processedDataRef.current.result;
        }
        
        // Log processing attempt
        console.log(`[ProductCategoryWidget] ${brand} - Processing data:`, {
            isLoading: loading,
            dataExists: Boolean(casesData),
            dataCount: casesData?.length || 0
        });
        
        // Skip processing if we've already done it for the same data
        if (processedDataRef.current && processedDataRef.current.originalData === casesData) {
            console.log(`[ProductCategoryWidget] ${brand} - Using cached processed data`);
            return processedDataRef.current.result;
        }
        
        // Check if only reference changed but data is identical (deep comparison)
        if (processedDataRef.current && casesData && processedDataRef.current.originalData && 
            JSON.stringify(casesData) === JSON.stringify(processedDataRef.current.originalData)) {
            // Update the reference but keep the result
            processedDataRef.current = {
                originalData: casesData,
                result: processedDataRef.current.result
            };
            return processedDataRef.current.result;
        }
        
        console.log(`[${componentId}] Processing new data, count: ${casesData?.length || 0}`);
        
        if (!casesData || !Array.isArray(casesData)) {
            return { processedData: [], detailedData: {} };
        }

        const categoryCount = {};
        const details = {};
        
        // Filter for the correct brand and categorize products
        casesData.forEach((item) => {
            if (item.Brand?.toLowerCase() === brand.toLowerCase()) {
                const category = brand.toLowerCase() === "apple" 
                    ? categorizeAppleProduct(item.ProdId)
                    : categorizeSamsungProduct(item.ProdId);
                
                categoryCount[category] = (categoryCount[category] || 0) + 1;
                
                if (!details[category]) {
                    details[category] = new Set();
                }
                details[category].add(item.ProdId);
            }
        });
        
        // Sort categories by count
        const sortedData = Object.entries(categoryCount)
            .sort((a, b) => b[1] - a[1])
            .map(([key, value]) => ({ name: key, value }));
        
        console.log(`[ProductCategoryWidget] ${brand} - Processed data:`, {
            categoriesFound: sortedData.length,
            totalItems: sortedData.reduce((sum, item) => sum + item.value, 0),
            categories: sortedData.map(item => item.name).join(', ')
        });
        
        // Store processed result in ref with reference to original data
        const result = { 
            processedData: sortedData,
            detailedData: details
        };
        processedDataRef.current = {
            originalData: casesData,
            result: result
        };
        // Store as last good data if valid
        if (sortedData.length > 0) {
            lastGoodDataRef.current = result;
        }
        return result;
    }, [casesData, brand, componentId, loading]);

    // --- FIX: Move fallback logic and hook to after memoizedData is declared ---
    const isFallback = memoizedData.processedData.length === 0 && lastGoodDataRef.current.processedData.length > 0;
    const displayData = memoizedData.processedData.length > 0 ? memoizedData : lastGoodDataRef.current;
    useEffect(() => {
        setFallbackActive(isFallback);
    }, [isFallback]);
    // --- END FIX ---

    // Completely stable data that never changes reference once set
    const stableData = useMemo(() => {
        if (!memoizedData.processedData.length) {
            return [];
        }
        
        if (!chartReadyRef.current) {
            console.log(`[${componentId}] Setting stable data for first time`);
            chartReadyRef.current = true;
            
            // Store chart data in a ref to make it completely stable
            processedDataRef.current = {
                ...processedDataRef.current,
                finalData: memoizedData.processedData,
                finalDetailedData: memoizedData.detailedData
            };
            
            // Only once we have good data, set the wasEverVisible flag
            if (!wasEverVisibleRef.current) {
                console.log(`[${componentId}] Chart ready for display`);
                wasEverVisibleRef.current = true;
            }
        }
        
        // Always return the same reference once set
        return processedDataRef.current?.finalData || [];
    }, [memoizedData, componentId]);
    
    const stableDetailedData = useMemo(() => {
        return processedDataRef.current?.finalDetailedData || {};
    }, []);

    // Add reference to track if fetch has been triggered
    const hasFetchedRef = useRef(false);
    // Track API request state to avoid duplicate calls
    const isRequestingRef = useRef(false);
    
    // Fix critical effect to load data only once and respect cache
    React.useEffect(() => {
        // If we already have data, mark fetch as complete and skip loading
        if (casesData && casesData.length > 0) {
            console.log(`[${componentId}] Data already available (${casesData.length} records), no fetch needed`);
            hasFetchedRef.current = true;
            isRequestingRef.current = false;
            
            // Ensure chart becomes visible
            if (!wasEverVisibleRef.current) {
                wasEverVisibleRef.current = true;
                setIsVisible(true);
                setChartKey(prevKey => prevKey + 1);
            }
            return;
        }
        
        // Continue with original logic for when we don't have data
        if (!casesData && !loading && !error && !hasFetchedRef.current && !isRequestingRef.current) {
            console.log(`[${componentId}] No data available and not loading, triggering fetch`);
            
            // Immediately mark as fetched AND requesting to prevent duplicate requests
            hasFetchedRef.current = true;
            isRequestingRef.current = true;
            
            // Wrap fetch in try/finally to ensure we reset the requesting flag
            try {
                // Execute the fetch
                fetchOpenCases();
            } finally {
                // Reset requesting flag after a short delay to avoid race conditions
                setTimeout(() => {
                    isRequestingRef.current = false;
                }, 500);
            }
        } else {
            // Log the reason we're skipping the fetch for debugging
            if (hasFetchedRef.current) {
                console.log(`[${componentId}] Already triggered a fetch previously`);
            } else if (isRequestingRef.current) {
                console.log(`[${componentId}] Request already in progress`);
            } else if (casesData) {
                console.log(`[${componentId}] Data already available, no fetch needed`);
                // Once we've verified data exists, mark as fetched
                hasFetchedRef.current = true;
            } else if (loading) {
                console.log(`[${componentId}] Data is currently loading, no fetch needed`);
            }
        }
    }, [casesData, loading, error, fetchOpenCases, componentId]);

    // Critical effect to initialize chart once we have data and are mounted
    React.useEffect(() => {
        if (memoizedData.processedData.length > 0 && !wasEverVisibleRef.current) {
            console.log(`[${componentId}] Data loaded, preparing chart for first render`);
            
            // Slight delay to let the DOM stabilize
            const timer = setTimeout(() => {
                wasEverVisibleRef.current = true;
                setIsVisible(true);
                
                // Force a fresh mount of the chart component
                setChartKey(k => k + 1);
            }, 200);
            
            return () => clearTimeout(timer);
        }
    }, [memoizedData.processedData.length, componentId]);

    // Fixed styles to prevent style object recreation
    const containerStyle = {
        width: "600px",
        height: "330px",
        backgroundColor: "#292929",
        padding: "20px",
        borderRadius: "8px"
    };

    const noDataContainerStyle = {
        ...containerStyle,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center"
    };

    // If emergency render limit is activated, show simplified view
    if (emergencyLimitActivated) {
        return (
            <div style={{ width: "600px", height: "330px", backgroundColor: "#292929", padding: "20px", borderRadius: "8px" }}>
                <h3>{brand} Products in Category</h3>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "270px" }}>
                    <div style={{ color: "#ff5555", textAlign: "center" }}>
                        <div style={{ marginBottom: "10px" }}>Display limited due to high render rate</div>
                        <button onClick={() => window.location.reload()}>Refresh Page</button>
                    </div>
                </div>
            </div>
        );
    }

    // Loading state - only show if no data exists
    if (loading && !casesData) {
        return (
            <div style={containerStyle}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80%" }}>
                    <Typography variant="h5" sx={{ color: "#fff" }}>Loading...</Typography>
                </div>
            </div>
        );
    }

    // Error state - offer a refresh button
    if (error && !memoizedData.processedData.length) {
        return (
            <div style={containerStyle}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "80%" }}>
                    <Typography variant="h5" sx={{ color: "#fff", mb: 2 }}>Error loading data</Typography>
                    <button onClick={fetchOpenCases} style={{ padding: '8px 16px' }}>Refresh Data</button>
                </div>
            </div>
        );
    }

    // No data state
    if (memoizedData.processedData.length === 0 && lastGoodDataRef.current.processedData.length === 0) {
        return (
            <div style={noDataContainerStyle}>
                <h3>No {brand} Products</h3>
            </div>
        );
    }

    // Normal render with data (now using displayData)
    return (
        <div style={{...containerStyle, position: 'relative'}}>
            <h3>{brand} Products in Category 
                {loading && (
                    <span style={{ fontSize: '0.7rem', color: '#ff9800', marginLeft: '5px' }}>
                        (refreshing)
                    </span>
                )}
            </h3>
            <Box sx={{ width: "100%", height: "270px", position: "relative" }}>
                {loading && (
                    <Box sx={{ 
                        position: "absolute", 
                        top: 5, 
                        right: 5, 
                        bgcolor: 'rgba(0,0,0,0.5)', 
                        color: '#aaa',
                        fontSize: '0.7rem',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        zIndex: 10
                    }}>
                        Updating...
                    </Box>
                )}
                {/* Only render chart once we have data and the visibility flag is set */}
                {wasEverVisibleRef.current && displayData.processedData.length > 0 && (
                    <Box sx={{ width: '100%', height: '100%' }}>
                        {/* Key ensures the chart completely remounts once with final data */}
                        <StablePieChart 
                            key={chartKey}
                            data={displayData.processedData} 
                            detailedData={displayData.detailedData} 
                        />
                    </Box>
                )}
                {/* Red dot indicator for fallback mode */}
                {fallbackActive && (
                    <Box sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: '#e53935',
                        border: '2px solid #fff',
                        zIndex: 20,
                    }} title="Viser sist tilgjengelige data" />
                )}
            </Box>
        </div>
    );
});

// Add this missing default export
export default ProductCategoryWidget;
